import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import {
  CalendarBlank, Users, CreditCard, TrendUp, Buildings, Barbell,
  Confetti, Ticket, ChartLine, Coins, Trophy, Info, X,
} from '@phosphor-icons/react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#A855F7', '#EF4444', '#06B6D4', '#84CC16', '#F97316'];

// Mirror of backend calculate_price() — keeps drill-down splits consistent
// with what Übersicht shows in totals.
const clientCourtPrice = (dateStr, startTime, durationMinutes, courtType = 'padel') => {
  if (!dateStr || !startTime) return 0;
  try {
    const dt = new Date(dateStr + 'T00:00:00');
    const weekday = dt.getDay(); // 0=Sun ... 6=Sat
    const [sh, sm] = (startTime || '0:0').split(':').map(n => parseInt(n) || 0);
    const startMin = sh * 60 + sm;
    const endMin = startMin + (durationMinutes || 60);
    const hours = (durationMinutes || 60) / 60;
    const boundary = 17 * 60;
    if (courtType === 'golf') {
      const before = 55, after = 65;
      if (endMin <= boundary) return Math.round(before * hours * 100) / 100;
      if (startMin >= boundary) return Math.round(after * hours * 100) / 100;
      const mb = boundary - startMin, ma = endMin - boundary;
      return Math.round(((before * mb / 60) + (after * ma / 60)) * 100) / 100;
    }
    // padel
    const isWeekend = (weekday === 0 || weekday === 6);
    if (isWeekend) return Math.round(60 * hours * 100) / 100;
    if (endMin <= boundary) return Math.round(50 * hours * 100) / 100;
    if (startMin >= boundary) return Math.round(64 * hours * 100) / 100;
    const mb = boundary - startMin, ma = endMin - boundary;
    return Math.round(((50 * mb / 60) + (64 * ma / 60)) * 100) / 100;
  } catch {
    return 0;
  }
};

const RANGES = [
  { v: 7, label: '7d' },
  { v: 30, label: '30d' },
  { v: 90, label: '90d' },
  { v: 365, label: '1J' },
];

const AdminDashboard = () => {
  const { language, API } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const dateLocale = language === 'de' ? de : enUS;
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // {kind, ...payload}
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [compareOn, setCompareOn] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get(`/admin/insights?days=${range}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, API]);

  const t = data?.totals;
  const pt = data?.previous_totals;

  const fmtCHF = (v) => `${(v || 0).toFixed(0)} CHF`;
  const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

  // Compute % delta vs previous period for any metric.
  // Returns {delta, sign, label} or null if comparison not meaningful (prev=0).
  const cmp = (curKey) => {
    if (!compareOn || !t || !pt) return null;
    const cur = t[curKey];
    const prev = pt[curKey];
    if (cur == null || prev == null) return null;
    if (prev === 0) {
      if (cur === 0) return { sign: 0, label: '0%' };
      return { sign: cur > 0 ? 1 : -1, label: cur > 0 ? '↑ neu' : '—' };
    }
    const pct = ((cur - prev) / Math.abs(prev)) * 100;
    const sign = pct > 0.5 ? 1 : pct < -0.5 ? -1 : 0;
    const label = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
    return { sign, label };
  };

  const heatmapMax = Math.max(1, ...(data?.heatmap || [0]));
  const typeSplitData = data ? Object.entries(data.booking_type_split).map(([k, v]) => ({
    name: k === 'training' ? L('Training', 'Training') : k === 'regular' ? L('Regular', 'Normal') : L('Other', 'Andere'),
    value: v,
  })).filter(d => d.value > 0) : [];

  // ── KPI tooltips & drill-downs ───────────────────────────────────────────
  const KPI_INFO = {
    'kpi-revenue': {
      tip: L(
        'Total revenue = Court Buchungen + Bar. Equals every CHF earned in the window.',
        'Gesamt-Umsatz = Court-Buchungen + Bar. Entspricht jedem im Zeitraum eingenommenen CHF.',
      ),
      drill: 'revenue_breakdown',
    },
    'kpi-court-revenue': {
      tip: L(
        'Money earned from the courts only: paid online padel bookings + Kasse Padelcourt entries + the court-rental portion deducted from every training (so trainings show pure profit under Akademie, not here).',
        'Einnahmen ausschliesslich von den Padel-Plätzen: bezahlte Online-Buchungen + Kassen-Einträge Padelcourt + der Court-Miet-Anteil, der von jedem Training abgezogen wird (Trainings zeigen reinen Profit unter Akademie, nicht hier).',
      ),
      drill: 'court_drill',
    },
    'kpi-bar-revenue': {
      tip: L(
        'Everything else from the Kasse — drinks, food, vouchers sold, Abos sold, racket rental etc. (all Kasse entries NOT in category Padelcourt).',
        'Alles uebrige aus der Kasse — Getraenke, Essen, verkaufte Gutscheine, verkaufte Abos, Racket-Miete usw. (alle Kassen-Eintraege AUSSER Kategorie Padelcourt).',
      ),
      drill: 'bar_breakdown',
    },
    'kpi-golf-revenue': {
      tip: L(
        'Revenue from the 2 golf simulators only — paid online golf bookings + Kasse Padelcourt entries that reference a golf court id.',
        'Umsatz ausschliesslich der 2 Golf-Simulatoren — bezahlte Golf-Buchungen online + Kassen-Eintraege Kategorie Padelcourt, die einem Golf-Court zugeordnet sind.',
      ),
      drill: 'golf_breakdown',
    },
    'kpi-akademie-revenue': {
      tip: L(
        'Pure training profit = total training amount minus the standard court rental for that time slot. The deducted court fee is moved to "Court". Includes all training bookings (junior + adult) and 10x training abos.',
        'Reiner Trainings-Profit = Trainings-Betrag minus Standard-Court-Miete für diese Zeit. Der abgezogene Court-Anteil wird unter "Court" erfasst. Inkl. Junior/Adult Trainings + 10x Trainings-Abos.',
      ),
      drill: 'akademie_breakdown',
    },
    'kpi-bookings': {
      tip: L('Total court bookings in the window (excluding cancelled).', 'Anzahl Platzbuchungen im Zeitraum (ohne stornierte).'),
      drill: 'bookings_list',
    },
    'kpi-utilization': {
      tip: L(
        'Average court hours booked per day across the whole club. Maximum is roughly 180 h/day (10 courts x ~18 opening hours).',
        'Durchschnittlich pro Tag gebuchte Court-Stunden im ganzen Club. Maximum sind ca. 180 Std/Tag (10 Courts x ca. 18 Oeffnungsstunden).',
      ),
      drill: 'court_breakdown',
    },
    'kpi-customers': {
      tip: L('All customer accounts on the platform (migrated + newly registered).', 'Alle Kundenkonten auf der Plattform (migriert + neu registriert).'),
    },
    'kpi-members': {
      tip: L('Active club members (blue tick) / total registered customers.', 'Aktive Clubmitglieder (blauer Haken) / total registrierte Kunden.'),
      drill: 'members_list',
    },
    'kpi-trainers': {
      tip: L('Active trainers/coaches available for training bookings.', 'Aktive Trainer / Coaches, verfügbar für Training-Buchungen.'),
      drill: 'trainers_list',
    },
    'kpi-juniors': {
      tip: L('Children registered in the Junior Academy.', 'In der Junior-Akademie registrierte Kinder.'),
      drill: 'juniors_list',
    },
    'kpi-events': {
      tip: L('Mixin / tournament / community events scheduled in the future.', 'Geplante Mixin / Turnier / Community Events in der Zukunft.'),
      drill: 'events_list',
    },
    'kpi-voucher': {
      tip: L(
        'Total open balance across every voucher that still has remaining credit. This is club money owed to customers.',
        'Offenes Guthaben aller noch nicht eingelösten Gutscheine. Das ist Geld, das der Club Kunden noch schuldet.',
      ),
      drill: 'vouchers_list',
    },
    'kpi-subs': {
      tip: L(
        'Open Abos (subscriptions): unused lessons remaining + their CHF value (lessons × price-per-lesson). This is service the club still owes.',
        'Offene Abos: verbleibende Lektionen + deren CHF-Wert (Lektionen × Preis pro Lektion). Diese Leistung schuldet der Club noch.',
      ),
      drill: 'subs_list',
    },
  };

  const openDrill = (kpiId) => {
    const info = KPI_INFO[kpiId];
    if (!info?.drill) return;
    setDrill({ kind: info.drill, kpiId });
    loadDrill(info.drill);
  };

  const loadDrill = async (kind) => {
    setDrillLoading(true);
    setDrillData(null);
    try {
      let payload = null;
      const start = data?.window?.start;
      const end = data?.window?.end;
      if (kind === 'bookings_list') {
        const res = await API.get(`/bookings?start_date=${start}&end_date=${end}`);
        payload = Array.isArray(res.data) ? res.data : [];
      } else if (kind === 'court_drill') {
        // Court tile: padel bookings NON-training + Padelcourt Kasse non-training
        // PLUS training rows showing only their court-rental portion (split).
        const [bk, ks, ct] = await Promise.all([
          API.get(`/bookings?start_date=${start}&end_date=${end}`),
          API.get(`/cash-register/entries?date=${start}&end_date=${end}`),
          API.get('/courts'),
        ]);
        const courtsList = ct.data || [];
        const golfIds = new Set(courtsList.filter(c => c.type === 'golf').map(c => c.id));
        const bookingsList = bk.data || [];
        const bkById = {};
        bookingsList.forEach(b => { bkById[b.id] = b; });
        const courtBookings = bookingsList
          .filter(b => b.payment_status === 'paid' && b.booking_type !== 'training' && !golfIds.has(b.court_id))
          .map(b => ({ ...b, _kind: 'booking', _court_share: b.amount }));
        const kasseRows = (ks.data || [])
          .filter(e => e.item_category === 'Padelcourt' && !golfIds.has(e.item_id))
          .map(e => {
            // Compute the COURT portion shown in this drill
            const isTraining = e.item_id === 'training_participant'
              || (e.item_id === 'booking_payment' && bkById[e.booking_id]?.booking_type === 'training');
            if (!isTraining) {
              return { ...e, _kind: 'kasse', _court_share: e.total_price };
            }
            // It's a training entry → split court/akademie
            const parent = bkById[e.booking_id];
            if (!parent) return null;
            const cp = clientCourtPrice(parent.date, parent.start_time, parent.duration_minutes, 'padel');
            const nP = Math.max(1, parent.participants || 1);
            const qty = Math.max(1, e.quantity || 1);
            const courtShare = e.item_id === 'training_participant'
              ? Math.min(e.total_price, (cp / nP) * qty)
              : Math.min(e.total_price, cp);
            return { ...e, _kind: 'kasse', _court_share: Math.round(courtShare * 100) / 100, _is_training: true };
          })
          .filter(Boolean)
          .filter(r => r._court_share > 0);
        payload = [...courtBookings, ...kasseRows];
      } else if (kind === 'members_list') {
        const res = await API.get('/users?is_member=true&limit=500');
        payload = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      } else if (kind === 'trainers_list') {
        const res = await API.get('/trainers');
        payload = (res.data || []).filter(t => t.is_active);
      } else if (kind === 'juniors_list') {
        const res = await API.get('/academy/juniors');
        payload = (res.data || []).filter(j => j.is_active !== false);
      } else if (kind === 'events_list') {
        const res = await API.get('/community/events?upcoming_only=true');
        payload = res.data || [];
      } else if (kind === 'vouchers_list') {
        const res = await API.get('/vouchers?status=active');
        payload = res.data || [];
      } else if (kind === 'subs_list') {
        const res = await API.get('/subscriptions?active_only=true');
        payload = res.data || [];
      } else if (kind === 'kasse_breakdown') {
        const res = await API.get(`/cash-register/entries?date=${start}&end_date=${end}`);
        payload = res.data || [];
      } else if (kind === 'bar_breakdown') {
        // Bar revenue: every Kasse entry NOT in category "Padelcourt"
        const res = await API.get(`/cash-register/entries?date=${start}&end_date=${end}`);
        const all = res.data || [];
        payload = all.filter(e => e.item_category !== 'Padelcourt');
      } else if (kind === 'golf_breakdown') {
        // Golf revenue: bookings on golf courts + Padelcourt Kasse entries
        // referencing a golf court. Easiest is to fetch courts then bookings.
        const [coursesRes, bookingsRes, kasseRes] = await Promise.all([
          API.get('/courts'),
          API.get(`/bookings?start_date=${start}&end_date=${end}`),
          API.get(`/cash-register/entries?date=${start}&end_date=${end}`),
        ]);
        const golfIds = new Set((coursesRes.data || []).filter(c => c.type === 'golf').map(c => c.id));
        const golfBookings = (bookingsRes.data || []).filter(b => golfIds.has(b.court_id))
          .map(b => ({ ...b, _kind: 'booking' }));
        const golfKasse = (kasseRes.data || []).filter(e => e.item_category === 'Padelcourt' && golfIds.has(e.item_id))
          .map(e => ({ ...e, _kind: 'kasse' }));
        payload = [...golfBookings, ...golfKasse];
      } else if (kind === 'akademie_breakdown') {
        // Akademie: training bookings + Padel Academy + training_participant + booking_payment (training)
        // Each row shows the akademie portion only (total – court rental).
        const [coursesRes, bookingsRes, kasseRes] = await Promise.all([
          API.get('/courts'),
          API.get(`/bookings?start_date=${start}&end_date=${end}`),
          API.get(`/cash-register/entries?date=${start}&end_date=${end}`),
        ]);
        const golfIds = new Set((coursesRes.data || []).filter(c => c.type === 'golf').map(c => c.id));
        const bookingsList = bookingsRes.data || [];
        const bkById = {};
        bookingsList.forEach(b => { bkById[b.id] = b; });
        const trainingBookings = bookingsList
          .filter(b => b.booking_type === 'training' && b.payment_status === 'paid')
          .map(b => {
            const cp = clientCourtPrice(b.date, b.start_time, b.duration_minutes, golfIds.has(b.court_id) ? 'golf' : 'padel');
            const courtShare = Math.min(b.amount || 0, cp);
            return { ...b, _kind: 'booking', _akademie_share: Math.round(((b.amount || 0) - courtShare) * 100) / 100 };
          });
        const kasseAk = (kasseRes.data || []).map(e => {
          if (e.item_category === 'Padel Academy') {
            return { ...e, _kind: 'kasse', _akademie_share: e.total_price };
          }
          if (e.item_category === 'Padelcourt') {
            const parent = bkById[e.booking_id];
            const isTraining = e.item_id === 'training_participant'
              || (e.item_id === 'booking_payment' && parent?.booking_type === 'training');
            if (isTraining && parent) {
              const cp = clientCourtPrice(parent.date, parent.start_time, parent.duration_minutes, golfIds.has(parent.court_id) ? 'golf' : 'padel');
              const nP = Math.max(1, parent.participants || 1);
              const qty = Math.max(1, e.quantity || 1);
              const courtShare = e.item_id === 'training_participant'
                ? Math.min(e.total_price, (cp / nP) * qty)
                : Math.min(e.total_price, cp);
              return { ...e, _kind: 'kasse', _akademie_share: Math.round((e.total_price - courtShare) * 100) / 100, _is_training: true };
            }
          }
          return null;
        }).filter(Boolean).filter(r => r._akademie_share > 0);
        payload = [...trainingBookings, ...kasseAk];
      } else if (kind === 'court_breakdown') {
        payload = data?.court_utilization || [];
      } else if (kind === 'revenue_breakdown') {
        payload = data?.revenue_series || [];
      }
      setDrillData(payload);
    } catch (e) {
      setDrillData([]);
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      {/* Header + range picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">
            {L('Business Insights', 'Geschäftsübersicht')}
          </h1>
          <p className="text-slate-500 text-sm">
            {data?.window
              ? `${format(parseISO(data.window.start), 'PP', { locale: dateLocale })} → ${format(parseISO(data.window.end), 'PP', { locale: dateLocale })}`
              : '...'}
            {compareOn && data?.previous_window && (
              <span className="ml-2 text-[11px] text-slate-400">
                {L('vs', 'vs.')} {format(parseISO(data.previous_window.start), 'd MMM', { locale: dateLocale })}–{format(parseISO(data.previous_window.end), 'd MMM', { locale: dateLocale })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCompareOn(v => !v)}
            className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border transition-colors ${
              compareOn ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
            data-testid="compare-toggle"
            title={L('Toggle comparison vs previous period', 'Vergleich zum vorherigen Zeitraum ein/aus')}
          >
            {L('vs prev.', 'vs. zuvor')}
          </button>
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1" data-testid="range-picker">
          {RANGES.map(r => (
            <button
              key={r.v}
              onClick={() => setRange(r.v)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                range === r.v ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'
              }`}
              data-testid={`range-${r.v}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        <Kpi loading={loading} label={L('Total revenue', 'Umsatz gesamt')} value={fmtCHF(t?.grand_revenue)} icon={Coins} color="text-emerald-600" bg="bg-emerald-50" testid="kpi-revenue" tooltip={KPI_INFO['kpi-revenue'].tip} onClick={() => openDrill('kpi-revenue')} delta={cmp('grand_revenue')} />
        <Kpi loading={loading} label={L('Court (8 padel)', 'Court (8 Padel)')} value={fmtCHF(t?.court_revenue)} icon={CalendarBlank} color="text-blue-600" bg="bg-blue-50" testid="kpi-court-revenue" tooltip={KPI_INFO['kpi-court-revenue'].tip} onClick={() => openDrill('kpi-court-revenue')} delta={cmp('court_revenue')} />
        <Kpi loading={loading} label={L('Golf (2 sim.)', 'Golf (2 Sim.)')} value={fmtCHF(t?.golf_revenue)} icon={Trophy} color="text-cyan-600" bg="bg-cyan-50" testid="kpi-golf-revenue" tooltip={KPI_INFO['kpi-golf-revenue'].tip} onClick={() => openDrill('kpi-golf-revenue')} delta={cmp('golf_revenue')} />
        <Kpi loading={loading} label={L('Akademie', 'Akademie')} value={fmtCHF(t?.akademie_revenue)} icon={Barbell} color="text-violet-600" bg="bg-violet-50" testid="kpi-akademie-revenue" tooltip={KPI_INFO['kpi-akademie-revenue'].tip} onClick={() => openDrill('kpi-akademie-revenue')} delta={cmp('akademie_revenue')} />
        <Kpi loading={loading} label={L('Bar', 'Bar')} value={fmtCHF(t?.bar_revenue)} icon={CreditCard} color="text-amber-600" bg="bg-amber-50" testid="kpi-bar-revenue" tooltip={KPI_INFO['kpi-bar-revenue'].tip} onClick={() => openDrill('kpi-bar-revenue')} delta={cmp('bar_revenue')} />
        <Kpi loading={loading} label={L('Bookings', 'Buchungen')} value={t?.bookings_count ?? 0} icon={ChartLine} color="text-purple-600" bg="bg-purple-50" testid="kpi-bookings" tooltip={KPI_INFO['kpi-bookings'].tip} onClick={() => openDrill('kpi-bookings')} delta={cmp('bookings_count')} />
        <Kpi loading={loading} label={L('Avg court hours / day', 'Ø Court-Std/Tag')} value={`${(t?.avg_hours_per_day ?? 0).toFixed(1)} h`} icon={Buildings} color="text-rose-600" bg="bg-rose-50" testid="kpi-utilization" tooltip={KPI_INFO['kpi-utilization'].tip} onClick={() => openDrill('kpi-utilization')} delta={cmp('avg_hours_per_day')} />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <SmallKpi loading={loading} label={L('Customer accounts', 'Kundenkonten')} value={t?.total_users ?? 0} icon={Users} testid="kpi-customers" tooltip={KPI_INFO['kpi-customers'].tip} onClick={() => openDrill('kpi-members')} />
        <SmallKpi loading={loading} label={L('Members', 'Mitglieder')} value={`${t?.total_members ?? 0} / ${t?.total_users ?? 0}`} icon={Users} testid="kpi-members" tooltip={KPI_INFO['kpi-members'].tip} onClick={() => openDrill('kpi-members')} />
        <SmallKpi loading={loading} label={L('Trainers', 'Trainer')} value={t?.trainers_active ?? 0} icon={Barbell} testid="kpi-trainers" tooltip={KPI_INFO['kpi-trainers'].tip} onClick={() => openDrill('kpi-trainers')} />
        <SmallKpi loading={loading} label={L('Juniors', 'Junioren')} value={t?.total_juniors ?? 0} icon={Trophy} testid="kpi-juniors" tooltip={KPI_INFO['kpi-juniors'].tip} onClick={() => openDrill('kpi-juniors')} />
        <SmallKpi loading={loading} label={L('Upcoming events', 'Anstehende Events')} value={t?.upcoming_events ?? 0} icon={Confetti} testid="kpi-events" tooltip={KPI_INFO['kpi-events'].tip} onClick={() => openDrill('kpi-events')} />
        <SmallKpi loading={loading} label={L('Open vouchers', 'Offene Gutscheine')} value={fmtCHF(t?.voucher_outstanding)} icon={Ticket} testid="kpi-voucher" tooltip={KPI_INFO['kpi-voucher'].tip} onClick={() => openDrill('kpi-voucher')} />
        <SmallKpi loading={loading} label={L('Open Abos', 'Offene Abos')} value={`${t?.subs_outstanding_lessons || 0} L · ${fmtCHF(t?.subs_outstanding_value)}`} icon={TrendUp} testid="kpi-subs" tooltip={KPI_INFO['kpi-subs'].tip} onClick={() => openDrill('kpi-subs')} />
      </div>

      {/* Revenue chart */}
      <Section title={L('Revenue trend', 'Umsatzverlauf')} testid="section-revenue-chart">
        <div className="h-[280px]">
          {loading ? <Skeleton /> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.revenue_series || []}>
                <defs>
                  <linearGradient id="g-bk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g-ks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g-ak" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A855F7" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#A855F7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }}
                  tickFormatter={(v) => format(parseISO(v), 'd MMM', { locale: dateLocale })}
                  minTickGap={30} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(v) => `${v}`} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => {
                    const labelMap = {
                      court: L('Court bookings', 'Court-Buchungen'),
                      bar: L('Bar', 'Bar'),
                      akademie: L('Akademie', 'Akademie'),
                    };
                    return [`${v.toFixed(0)} CHF`, labelMap[n] || n];
                  }}
                  labelFormatter={(v) => format(parseISO(v), 'PP', { locale: dateLocale })}
                />
                <Area type="monotone" dataKey="court" stroke="#3B82F6" fill="url(#g-bk)" strokeWidth={2} />
                <Area type="monotone" dataKey="akademie" stroke="#A855F7" fill="url(#g-ak)" strokeWidth={2} />
                <Area type="monotone" dataKey="bar" stroke="#F59E0B" fill="url(#g-ks)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      {/* Hours booked per day */}
      <Section title={L('Padel hours booked per day', 'Padel-Stunden pro Tag')} testid="section-hours-chart">
        <p className="text-xs text-slate-500 mb-3">
          {L(
            `Padel courts only (golf tracked separately). Out of ${t?.available_hours_per_day || 144} h available per day across the 8 padel courts. Average so far: ${(t?.avg_hours_per_day || 0).toFixed(1)} h/day. Golf usage: Ø ${(t?.avg_golf_hours_per_day || 0).toFixed(1)} h/day.`,
            `Nur Padel-Plätze (Golf separat). Von ${t?.available_hours_per_day || 144} verfügbaren Std/Tag über die 8 Padel-Plätze. Schnitt bisher: ${(t?.avg_hours_per_day || 0).toFixed(1)} Std/Tag. Golf: Ø ${(t?.avg_golf_hours_per_day || 0).toFixed(1)} Std/Tag.`,
          )}
        </p>
        <div className="h-[240px]">
          {loading ? <Skeleton /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.hours_series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }}
                  tickFormatter={(v) => format(parseISO(v), 'd MMM', { locale: dateLocale })}
                  minTickGap={28} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(v) => `${v}h`} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n, p) => {
                    if (n === 'booked_hours') {
                      const pct = p?.payload?.utilization_pct ?? 0;
                      return [`${v.toFixed(1)} h (${pct.toFixed(1)}%)`, L('Booked', 'Gebucht')];
                    }
                    return [`${v} h`, L('Available', 'Verfügbar')];
                  }}
                  labelFormatter={(v) => format(parseISO(v), 'PP', { locale: dateLocale })}
                />
                <Bar dataKey="booked_hours" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={L('Court utilization', 'Platzauslastung')} testid="section-court-util">
          <div className="space-y-2">
            {(data?.court_utilization || []).map((c) => {
              const pct = c.utilization;
              const tone = pct >= 60 ? 'bg-emerald-500' : pct >= 30 ? 'bg-amber-400' : 'bg-slate-300';
              return (
                <div key={c.id} data-testid={`court-util-${c.id}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium flex items-center gap-1.5">
                      {c.name}
                      {c.type === 'golf' && <span className="text-[9px] font-bold uppercase px-1 rounded bg-amber-100 text-amber-700">Golf</span>}
                    </span>
                    <span className="text-slate-500 text-xs tabular-nums">
                      {c.booked_hours}h · <strong className="text-slate-800">{pct.toFixed(1)}%</strong>
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
            {(!data?.court_utilization || data.court_utilization.length === 0) && !loading && (
              <p className="text-sm text-slate-400">{L('No data', 'Keine Daten')}</p>
            )}
          </div>
        </Section>

        {/* Hour-of-day heatmap */}
        <Section title={L('Booking peak hours', 'Stoßzeiten')} testid="section-heatmap">
          <div className="grid grid-cols-12 gap-0.5">
            {(data?.heatmap || []).slice(6, 24).map((count, idx) => {
              const hour = idx + 6;
              const intensity = count / heatmapMax;
              const opacity = intensity === 0 ? 0.08 : Math.max(0.15, intensity);
              return (
                <div key={hour} className="flex flex-col items-center gap-0.5" title={`${hour}:00 — ${count}`}>
                  <div
                    className="w-full aspect-square rounded"
                    style={{ background: `rgba(16, 185, 129, ${opacity})` }}
                    data-testid={`heat-${hour}`}
                  />
                  <span className="text-[9px] text-slate-400 tabular-nums">{hour}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            {L(`Distribution of ${data?.totals?.bookings_count || 0} bookings by start hour`,
              `Verteilung von ${data?.totals?.bookings_count || 0} Buchungen nach Startzeit`)}
          </p>
        </Section>

        {/* Booking type split — pie */}
        <Section title={L('Booking type split', 'Buchungs-Mix')} testid="section-type-split">
          <div className="h-[220px]">
            {loading || typeSplitData.length === 0 ? (
              loading ? <Skeleton /> : <p className="text-sm text-slate-400">{L('No data', 'Keine Daten')}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeSplitData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}
                    paddingAngle={2}>
                    {typeSplitData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>

        {/* Top coaches */}
        <Section title={L('Top coaches', 'Top Trainer')} testid="section-top-coaches">
          {loading ? <Skeleton /> : (data?.top_coaches?.length || 0) === 0 ? (
            <p className="text-sm text-slate-400">{L('No training bookings yet', 'Noch keine Training-Buchungen')}</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_coaches.map((c, i) => {
                const max = data.top_coaches[0]?.revenue || 1;
                const pct = (c.revenue / max) * 100;
                return (
                  <div key={c.trainer_id} className="relative" data-testid={`coach-row-${c.trainer_id}`}>
                    <div className="flex items-center justify-between text-sm py-1.5 px-2.5 rounded-lg overflow-hidden">
                      <div className="absolute inset-0 bg-blue-100/40" style={{ width: `${pct}%`, transition: 'width .3s' }} />
                      <span className="relative flex items-center gap-2 truncate">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold ${i < 3 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                          {i + 1}
                        </span>
                        <span className="font-semibold text-slate-800 truncate">{c.trainer_name}</span>
                        <span className="text-[10px] text-slate-400 hidden sm:inline">{c.sessions} {L('sessions', 'Lekt.')} · {c.hours}h</span>
                      </span>
                      <span className="relative font-bold text-slate-700 tabular-nums">{c.revenue.toFixed(0)} CHF</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top kasse items */}
        <Section title={L('Top items (Kasse)', 'Top-Artikel (Kasse)')} testid="section-top-items">
          {loading ? <Skeleton /> : (data?.top_items?.length || 0) === 0 ? (
            <p className="text-sm text-slate-400">{L('No sales yet', 'Noch keine Verkäufe')}</p>
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_items.slice(0, 8)} layout="vertical" margin={{ left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} width={140} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [`${v.toFixed(0)} CHF`, L('Revenue', 'Umsatz')]}
                  />
                  <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        {/* Top customers */}
        <Section title={L('Top customers', 'Top-Kunden')} testid="section-top-customers">
          {loading ? <Skeleton /> : (data?.top_customers?.length || 0) === 0 ? (
            <p className="text-sm text-slate-400">{L('No customers yet', 'Noch keine Kunden')}</p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="py-2 px-2">#</th>
                    <th className="py-2 px-2">{L('Name', 'Name')}</th>
                    <th className="py-2 px-2 text-right">{L('Bookings', 'Buchungen')}</th>
                    <th className="py-2 px-2 text-right">{L('Hours', 'Std.')}</th>
                    <th className="py-2 px-2 text-right">{L('Spent', 'Ausgegeben')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_customers.map((c, i) => (
                    <tr key={c.user_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`customer-row-${c.user_id}`}>
                      <td className="py-2 px-2 text-slate-400 text-xs">{i + 1}</td>
                      <td className="py-2 px-2 text-slate-700 font-medium truncate max-w-[180px]">{c.user_name}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums">{c.bookings}</td>
                      <td className="py-2 px-2 text-right text-slate-500 tabular-nums">{c.hours}h</td>
                      <td className="py-2 px-2 text-right text-emerald-700 font-bold tabular-nums">{c.spent.toFixed(0)} CHF</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {/* ====== KPI drill-down dialog ====== */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="kpi-drill-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {drill && drillTitle(drill.kind, L)}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              {drill && KPI_INFO[drill.kpiId]?.tip}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            {drillLoading ? (
              <div className="space-y-2 py-6">
                {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : (
              <DrillContent kind={drill?.kind} data={drillData} L={L} dateLocale={dateLocale} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const drillTitle = (kind, L) => {
  switch (kind) {
    case 'bookings_list': return L('All bookings in this window', 'Alle Buchungen im Zeitraum');
    case 'court_drill': return L('Court revenue (padel only, training excluded)', 'Court-Umsatz (nur Padel, ohne Trainings)');
    case 'kasse_breakdown': return L('Kasse entries', 'Kassen-Einträge');
    case 'bar_breakdown': return L('Bar entries (Kasse without court fees)', 'Bar-Einträge (Kasse ohne Court-Gebühren)');
    case 'golf_breakdown': return L('Golf simulator usage', 'Golf-Simulator Nutzung');
    case 'akademie_breakdown': return L('Akademie revenue (training)', 'Akademie-Umsatz (Trainings)');
    case 'court_breakdown': return L('Court utilization detail', 'Auslastung je Platz');
    case 'revenue_breakdown': return L('Daily revenue split', 'Tägliche Umsatzaufteilung');
    case 'members_list': return L('Active members', 'Aktive Mitglieder');
    case 'trainers_list': return L('Active trainers', 'Aktive Trainer');
    case 'juniors_list': return L('Junior Academy roster', 'Junior-Akademie Liste');
    case 'events_list': return L('Upcoming community events', 'Anstehende Community-Events');
    case 'vouchers_list': return L('Active vouchers', 'Aktive Gutscheine');
    case 'subs_list': return L('Active subscriptions (Abos)', 'Aktive Abos');
    default: return L('Details', 'Details');
  }
};

const DrillContent = ({ kind, data, L, dateLocale }) => {
  if (!data) return null;
  const list = Array.isArray(data) ? data : [];
  if (list.length === 0) return <p className="text-sm text-slate-400 py-4">{L('No records', 'Keine Einträge')}</p>;
  if (kind === 'bookings_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'date', h: L('Date', 'Datum') },
      { k: 'start_time', h: L('Time', 'Zeit') },
      { k: 'court_name', h: L('Court', 'Court') },
      { k: 'user_name', h: L('Customer', 'Kunde') },
      { k: 'booking_type', h: L('Type', 'Typ') },
      { k: 'amount', h: 'CHF', r: true, f: (v) => `${(v || 0).toFixed(0)}` },
      { k: 'payment_status', h: L('Pay', 'Zahlung') },
    ]} />;
  }
  if (kind === 'kasse_breakdown' || kind === 'bar_breakdown') {
    return <SimpleTable rows={list.slice(0, 300)} cols={[
      { k: 'date', h: L('Date', 'Datum') },
      { k: 'item_name', h: L('Item', 'Artikel') },
      { k: 'item_category', h: L('Category', 'Kategorie') },
      { k: 'quantity', h: 'Qty', r: true },
      { k: 'total_price', h: 'CHF', r: true, f: (v) => `${(v || 0).toFixed(2)}` },
      { k: 'payment_method', h: L('Method', 'Zahlart') },
    ]} />;
  }
  if (kind === 'golf_breakdown') {
    return <SimpleTable rows={list.slice(0, 300)} cols={[
      { k: 'date', h: L('Date', 'Datum') },
      { k: '_kind', h: L('Source', 'Quelle'), f: (v) => v === 'kasse' ? 'Kasse' : 'Booking' },
      { k: 'start_time', h: L('Time', 'Zeit'), f: (v, row) => v || row.created_at?.slice(11, 16) || '—' },
      { k: 'item_name', h: L('Item', 'Item'), f: (v, row) => v || row.court_name || '—' },
      { k: 'user_name', h: L('Customer', 'Kunde'), f: (v) => v || '—' },
      { k: 'amount', h: 'CHF', r: true, f: (v, row) => ((v != null ? v : row.total_price) || 0).toFixed(2) },
    ]} />;
  }
  if (kind === 'court_drill') {
    const total = list.reduce((s, r) => s + (r._court_share || 0), 0);
    return <>
      <p className="text-xs text-slate-500 mb-2">{L(`Total: ${total.toFixed(2)} CHF — for trainings only the court-rental portion is counted here.`,
        `Total: ${total.toFixed(2)} CHF — bei Trainings wird hier nur der Court-Miet-Anteil gezählt.`)}</p>
      <SimpleTable rows={list.slice(0, 300)} cols={[
        { k: 'date', h: L('Date', 'Datum') },
        { k: '_kind', h: L('Source', 'Quelle'), f: (v) => v === 'kasse' ? 'Kasse' : 'Booking' },
        { k: 'start_time', h: L('Time', 'Zeit'), f: (v, row) => v || '—' },
        { k: 'court_name', h: 'Court', f: (v, row) => v || row.item_name || '—' },
        { k: 'user_name', h: L('Customer', 'Kunde'), f: (v) => v || '—' },
        { k: '_court_share', h: L('Court CHF', 'Court CHF'), r: true, f: (v, row) => {
          const total = (row.amount != null ? row.amount : row.total_price) || 0;
          if (row._is_training && total !== v) return `${(v || 0).toFixed(2)} / ${total.toFixed(0)}`;
          return (v || 0).toFixed(2);
        } },
      ]} />
    </>;
  }
  if (kind === 'akademie_breakdown') {
    const total = list.reduce((s, r) => s + (r._akademie_share || 0), 0);
    return <>
      <p className="text-xs text-slate-500 mb-2">{L(`Total: ${total.toFixed(2)} CHF — pure trainer revenue (court rental already moved to Court).`,
        `Total: ${total.toFixed(2)} CHF — reiner Trainer-Umsatz (Court-Miete wurde bereits dem Court-Topf zugeordnet).`)}</p>
      <SimpleTable rows={list.slice(0, 300)} cols={[
        { k: 'date', h: L('Date', 'Datum') },
        { k: '_kind', h: L('Source', 'Quelle'), f: (v) => v === 'kasse' ? 'Kasse' : 'Booking' },
        { k: 'item_name', h: L('Item', 'Beschreibung'), f: (v, row) => v || row.trainer_name || (row.booking_type === 'training' ? `Training · ${row.start_time || ''}` : '—') },
        { k: 'item_category', h: L('Type', 'Typ'), f: (v, row) => v || row.booking_type || '—' },
        { k: 'user_name', h: L('Customer', 'Kunde'), f: (v) => v || '—' },
        { k: '_akademie_share', h: L('Akademie CHF', 'Akademie CHF'), r: true, f: (v, row) => {
          const total = (row.amount != null ? row.amount : row.total_price) || 0;
          if (row._is_training && total !== v) return `${(v || 0).toFixed(2)} / ${total.toFixed(0)}`;
          return (v || 0).toFixed(2);
        } },
      ]} />
    </>;
  }
  if (kind === 'court_breakdown') {
    return <SimpleTable rows={list} cols={[
      { k: 'name', h: 'Court' },
      { k: 'type', h: L('Type', 'Typ') },
      { k: 'bookings', h: L('Bookings', 'Buch.'), r: true },
      { k: 'booked_hours', h: 'Std.', r: true },
      { k: 'utilization', h: '%', r: true, f: (v) => `${v.toFixed(1)}` },
    ]} />;
  }
  if (kind === 'revenue_breakdown') {
    return <SimpleTable rows={list} cols={[
      { k: 'date', h: L('Date', 'Datum') },
      { k: 'bookings', h: L('Bookings CHF', 'Buchungen CHF'), r: true, f: (v) => v.toFixed(2) },
      { k: 'kasse', h: L('Kasse CHF', 'Kasse CHF'), r: true, f: (v) => v.toFixed(2) },
      { k: 'total', h: 'Total CHF', r: true, f: (v) => v.toFixed(2) },
    ]} />;
  }
  if (kind === 'members_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'name', h: L('Name', 'Name') },
      { k: 'email', h: 'Email' },
      { k: 'phone', h: L('Phone', 'Telefon') },
      { k: 'player_level', h: 'Lvl', r: true },
    ]} />;
  }
  if (kind === 'trainers_list') {
    return <SimpleTable rows={list} cols={[
      { k: 'name', h: L('Name', 'Name') },
      { k: 'email', h: 'Email' },
      { k: 'phone', h: L('Phone', 'Telefon') },
    ]} />;
  }
  if (kind === 'juniors_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'name', h: L('Name', 'Name') },
      { k: 'birth_year', h: L('Year', 'Jahr'), r: true },
      { k: 'parent_name', h: L('Parent', 'Eltern') },
      { k: 'parent_phone', h: L('Phone', 'Telefon') },
    ]} />;
  }
  if (kind === 'events_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'date', h: L('Date', 'Datum') },
      { k: 'start_time', h: L('Time', 'Zeit') },
      { k: 'title', h: L('Title', 'Titel') },
      { k: 'type', h: L('Type', 'Typ') },
      { k: 'confirmed_count', h: L('Reg.', 'Anm.'), r: true },
      { k: 'max_participants', h: 'Max', r: true },
    ]} />;
  }
  if (kind === 'vouchers_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'code', h: 'Code' },
      { k: 'user_name', h: L('Name', 'Name') },
      { k: 'amount', h: 'CHF', r: true, f: (v) => (v || 0).toFixed(2) },
      { k: 'remaining_balance', h: L('Remaining', 'Offen'), r: true, f: (v, row) => ((v != null ? v : row.amount) || 0).toFixed(2) },
    ]} />;
  }
  if (kind === 'subs_list') {
    return <SimpleTable rows={list.slice(0, 200)} cols={[
      { k: 'user_name', h: L('Customer', 'Kunde') },
      { k: 'type', h: L('Type', 'Typ') },
      { k: 'lessons_remaining', h: L('Left', 'Offen'), r: true },
      { k: 'total_lessons', h: 'Total', r: true },
      { k: 'price_per_lesson', h: 'CHF/Lekt.', r: true, f: (v) => (v || 0).toFixed(0) },
    ]} />;
  }
  return null;
};

const SimpleTable = ({ rows, cols }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
          {cols.map(c => (
            <th key={c.k} className={`py-2 px-2 ${c.r ? 'text-right' : ''}`}>{c.h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
            {cols.map(c => {
              const raw = row[c.k];
              const v = c.f ? c.f(raw, row) : (raw == null ? '—' : String(raw));
              return (
                <td key={c.k} className={`py-1.5 px-2 ${c.r ? 'text-right tabular-nums font-semibold' : 'text-slate-700'}`}>
                  {v}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length >= 200 && (
      <p className="text-[10px] text-slate-400 mt-2 text-center italic">
        Showing first 200 entries — refine the date range to see more.
      </p>
    )}
  </div>
);

const Kpi = ({ label, value, icon: Icon, color, bg, loading, testid, tooltip, onClick, delta }) => (
  <motion.button
    type="button"
    onClick={onClick}
    title={tooltip}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    className="group relative bg-white border border-slate-200/60 rounded-xl p-4 flex flex-col gap-2 shadow-sm text-left transition-all hover:border-emerald-300 hover:shadow-md active:scale-[0.99] cursor-pointer"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1">
        {label}
        {tooltip && <Info size={11} className="text-slate-300 group-hover:text-emerald-500 transition-colors" weight="duotone" />}
      </span>
      <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
        <Icon size={16} className={color} weight="duotone" />
      </div>
    </div>
    <span className="text-2xl font-extrabold text-slate-900 tabular-nums leading-tight">
      {loading ? <span className="inline-block w-16 h-6 bg-slate-100 rounded animate-pulse" /> : value}
    </span>
    {delta && !loading && (
      <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-md w-fit ${
        delta.sign > 0 ? 'bg-emerald-50 text-emerald-700' :
        delta.sign < 0 ? 'bg-rose-50 text-rose-700' :
        'bg-slate-100 text-slate-500'
      }`} data-testid={`${testid}-delta`}>
        {delta.sign > 0 && '↑'}{delta.sign < 0 && '↓'}{delta.sign === 0 && '·'} {delta.label}
      </span>
    )}
  </motion.button>
);

const SmallKpi = ({ label, value, icon: Icon, loading, testid, tooltip, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={tooltip}
    className="group bg-white border border-slate-200/60 rounded-lg px-3 py-2.5 flex items-center gap-2.5 text-left transition-all hover:border-emerald-300 hover:shadow-sm active:scale-[0.99] cursor-pointer"
    data-testid={testid}
  >
    <Icon size={18} className="text-slate-400 shrink-0 group-hover:text-emerald-500 transition-colors" weight="duotone" />
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-tight truncate flex items-center gap-1">
        {label}
        {tooltip && <Info size={9} className="text-slate-300 group-hover:text-emerald-500 transition-colors shrink-0" weight="duotone" />}
      </p>
      <p className="text-sm font-bold text-slate-800 tabular-nums truncate">
        {loading ? <span className="inline-block w-14 h-3.5 bg-slate-100 rounded animate-pulse" /> : value}
      </p>
    </div>
  </button>
);

const Section = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-5" data-testid={testid}>
    <h3 className="text-base font-bold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const Skeleton = () => (
  <div className="space-y-2">
    {[1,2,3,4].map(i => (
      <div key={i} className="h-3 rounded bg-slate-100 animate-pulse" style={{ width: `${100 - i * 12}%` }} />
    ))}
  </div>
);

export default AdminDashboard;
